import { useContainerContext } from '@atlassian/jira-providers-container-context/src/main.tsx';
import type { ProjectContext } from '@atlassian/jira-providers-container-context/src/types.tsx';
import {
	toProjectId,
	toProjectKey,
	type ProjectId,
	type ProjectKey,
} from '@atlassian/jira-shared-types/src/general.tsx';
import { usePathParam } from '@atlassian/react-resource-router';

const useProjectKey = (): ProjectKey => {
	const [projectKey] = usePathParam('projectKey');
	if (projectKey == null) {
		throw new Error('Project key missing');
	}
	return toProjectKey(projectKey);
};

const useProjectContext = (): ProjectContext | null => {
	const [{ data: containerContext }] = useContainerContext();
	if (containerContext == null || containerContext.contextType !== 'project') {
		return null;
	}
	return containerContext;
};

const useProjectId = (): ProjectId | null => {
	const projectContext = useProjectContext();
	return projectContext === null ? null : toProjectId(`${projectContext.project.id}`);
};

const useProjectName = (): string | null => {
	const projectContext = useProjectContext();
	return projectContext === null ? null : projectContext.project.name;
};

const useIsSimplified = (): boolean | null => {
	const projectContext = useProjectContext();
	return projectContext === null ? null : projectContext.project.isSimplified;
};

type ProjectDetails = {
	// Guaranteed to be immediately available.
	key: ProjectKey;
	// May resolve after a delay.
	id: ProjectId | null;
	// May resolve after a delay.
	name: string | null;
	// May resolve after a delay.
	isSimplified: boolean | null;
};

export const useProjectDetails = (): ProjectDetails => {
	const id = useProjectId();
	const key = useProjectKey();
	const name = useProjectName();
	const isSimplified = useIsSimplified();
	return { id, key, name, isSimplified };
};
